import { AdminStatus } from "./admin-service";
import { ApplicationFormDTO } from "./application-types";
import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  WrappedCashifyFunction,
  GenericResponse,
  WrappedCashifyPageableQuery,
  CommonResponse,
} from "./utils";

export interface ApplicationGroupService {
  listGroup: ReturnType<typeof listApplicationGroup>;
  createGroup: ReturnType<typeof createApplicationGroup>;
  getGroupDetail: ReturnType<typeof getApplicationGroupDetail>;
  putGroupDetail: ReturnType<typeof putApplicationGroupDetail>;
  deleteGroupDetail: ReturnType<typeof deleteApplicationGroupDetail>;
  listSelfGroup: ReturnType<typeof listSelfGroup>;
}

export interface ApplicationGroupSearchCriteria {
  id?: number;
  groupName?: string;
  withAgentId?: number;
  createAdmin?: string;
  "createTime.from"?: string;
  "createTime.to"?: string;
  lastUpdateAdmin?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
}

export interface ApplicationAgent {
  id: number;
  name: string;
  emailAddress: string;
  status: AdminStatus;
}

export interface ApplicationGroupDTO {
  id: number;
  tenantId: number;
  groupName: string;
  associatedAgentIds: number[];
  applicationAgents: ApplicationAgent[];
  createAdmin: string;
  createTime: string;
  lastUpdateAdmin: string;
  lastUpdateTime: string;
}

export interface ApplicationGroupCreateRequest {
  groupName: string;
  associatedAgentIds?: number[];
}
export interface ApplicationGroupDetail extends ApplicationGroupDTO {
  associatedForms: ApplicationFormDTO[];
}

export interface ApplicationGroupUpdateRequest {
  groupId: string;
  groupName?: string;
  associatedAgentIds?: number[];
}

export const listApplicationGroup: WrappedCashifyPageableQuery<
  ApplicationGroupSearchCriteria,
  ApplicationGroupDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application-group`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createApplicationGroup: WrappedCashifyFunction<
  ApplicationGroupCreateRequest,
  GenericResponse<ApplicationGroupDTO>
> = ({ urlPrefix, options }) => {
  return async (body) => {
    const url = `${urlPrefix}/api/v1/application-group`;
    const postOpts = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getApplicationGroupDetail: WrappedCashifyFunction<
  { groupId: string },
  GenericResponse<ApplicationGroupDetail>
> = ({ urlPrefix, options }) => {
  return async ({ groupId }) => {
    let url = `${urlPrefix}/api/v1/application-group/${groupId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putApplicationGroupDetail: WrappedCashifyFunction<
  ApplicationGroupUpdateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ groupId, groupName, associatedAgentIds }) => {
    let url = `${urlPrefix}/api/v1/application-group/${groupId}`;
    const body = {
      groupName,
      associatedAgentIds,
    };
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteApplicationGroupDetail: WrappedCashifyFunction<
  { groupId: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ groupId }) => {
    let url = `${urlPrefix}/api/v1/application-group/${groupId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listSelfGroup: WrappedCashifyFunction<
  { groupName?: string },
  GenericResponse<ApplicationGroupDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ groupName }) => {
    let url = `${urlPrefix}/api/v1/application-group/self`;
    if (!!groupName) {
      url = getURLWithQueryParams(url, {
        groupName,
      });
    }
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
